import { Box, Tooltip, Typography } from "@mui/material";
import { type ReactNode } from "react";

import { formatMismatchMonthRanges, formatPeriodSpan, monthsInSpan } from "./periodFormat";

/** Ranges shown in the tooltip before the list truncates; the drawer lists them all. */
const TOOLTIP_MAX_RANGES = 6;

type PeriodRow = {
  periodStart: string;
  lastPeriodStart?: string | null;
  mismatchMonths?: number | null;
  mismatchPeriodStarts?: readonly string[] | null;
};

export function PeriodCell({ row, tabIndex }: { row: PeriodRow; tabIndex?: number }) {
  const span = formatPeriodSpan(row);
  const total = monthsInSpan(row);
  const count = row.mismatchMonths;
  if (count == null) return <>{span}</>;

  const hasAgreeingMonths = count < total;
  const months = row.mismatchPeriodStarts ?? [];
  let tooltip: ReactNode = null;
  if (hasAgreeingMonths) {
    const ranges = formatMismatchMonthRanges(months);
    const visibleRanges = ranges.slice(0, TOOLTIP_MAX_RANGES);
    const remainingRanges = ranges.length - visibleRanges.length;
    tooltip = ranges.length ? (
      <Box>
        <Typography variant="caption" component="div" fontWeight={600} mb={0.5}>
          Mismatch months
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {visibleRanges.map((range) => (
            <li key={range}>{range}</li>
          ))}
          {remainingRanges > 0 && <li>+{remainingRanges} more</li>}
        </Box>
      </Box>
    ) : (
      "Mismatch month details aren't available"
    );
  }

  const cell = (
    <Box component="span" tabIndex={tooltip ? tabIndex : undefined} sx={{ whiteSpace: "nowrap" }}>
      {span}
      <Typography component="span" variant="body2" color="text.secondary">
        {hasAgreeingMonths ? ` · ${count} of ${total} mo` : ` · ${count} mo`}
      </Typography>
    </Box>
  );
  return tooltip ? (
    <Tooltip title={tooltip} describeChild>
      {cell}
    </Tooltip>
  ) : (
    cell
  );
}
